/* components/LoadMore */

/*
    The LoadMore component displays a button for a paginated number of products
    which loads the next page in to a specified container

    There is also a LoadPrevious component, which is similar,
    and if this exists on the page we subscribe to an event that
    allows us to keep other elements up to date such as the percentage
    bar and numner of elements shown
*/

import { Component, Ref, Prop } from '@eastsideco/salvo-ts';
import { ThemeComponent } from "@/theme/ThemeComponent";
import { LoadPrevious } from "../LoadPrevious/LoadPrevious";
import axios from 'axios';

@Component
export class LoadMore extends ThemeComponent {

    @Prop() currentPage!: number;
    @Prop() paginateUrl!: string;
    @Prop({ default: '' }) activeFilters!: string;
    @Prop({ default: '' }) activeSorting!: string;
    @Prop() text!: string;
    @Prop() loadingText!: string;
    @Prop() pageSize!: number;
    @Prop() pageTotal!: number;
    @Prop() progressStart!: number;
    @Prop() progressEnd!: number;
    @Prop() progressTotal!: number;
    @Prop() itemContainerSelector!: string;
    @Prop() itemSelector!: string;
    @Ref({ required: false }) button!: HTMLButtonElement;
    @Ref({ required: false }) progressBar!: HTMLElement;
    @Ref({ required: false }) range!: HTMLDivElement;
    @Ref({ required: false }) paginationWrapper!: HTMLDivElement;

    private itemContainer!: HTMLElement|null;
    private loadPrevious!: LoadPrevious|null;

    init() {
        // Get the element where items will be added
        this.itemContainer = document.querySelector(this.itemContainerSelector);
        this.loadPrevious = document.querySelector('load-previous');

        // Listen for clicks
        this.$listen(this, 'click', () => {
            // Set loading text
            this.button.innerHTML = this.loadingText;
            // Load more/previous
            this.loadMore();
        });

        // If a LoadPrevious component exists, subscribe
        // to updates
        if (this.loadPrevious) {
            this.$subscribe(this.loadPrevious.onPreviousLoad, () => {

                // Reduce the pagination start by page size
                this.progressStart = this.progressStart - this.pageSize;

                // If a percentage bar is displayed, update it
                if (this.progressBar) {
                    this.updateBarPercentage(this.progressStart, this.progressEnd, this.progressTotal);
                }

                // Update all elements displaying the pagination start number
                this.reducePaginationStart();
            });
        }
    }

    loadMore() {
        if (this.paginateUrl) {
            let url = `${this.paginateUrl}?page=${this.currentPage + 1}`;

            // If there is an active sort by option (passed as prop), then include this in the next page request
            if (this.activeSorting != '') {
                url = `${url}&sort_by=${this.activeSorting}`;
            }

            // If there are any active filters (passed as prop), then include this in the next page request
            if (this.activeFilters !== '') {
                url = `${url}&${this.activeFilters}`;
            }

            // Make request for the next page
            axios.get(url).then((response) => {

                // Get new elements from response data and add them to a new elements wrapper
                const newElementsWrapper = document.createElement('div');
                newElementsWrapper.innerHTML = response.data;

                // Update the current page
                this.currentPage ++;

                // Add new elements to page
                // eslint-disable-next-line no-underscore-dangle
                const newElements = [...newElementsWrapper.querySelectorAll(this.itemSelector)];
                this.addNewElements(newElements);

                // Ensure all references to number of elements shown are updated
                this.increasePaginationEnd();

                // If a percentage bar is displayed, update it
                if (this.progressBar) {
                    this.updateBarPercentage(this.progressStart, this.progressEnd, this.progressTotal);
                }

                // Update page query string in URL
                const url = new URL(String(window.location));
                url.searchParams.set('page', String(this.currentPage));
                window.history.pushState(null, '', url.toString());

                // If the current page is the last page, hide the load previous button
                if (this.currentPage == this.pageTotal) {
                    this.button.style.display = 'none';
                }

            }).catch((error) => {
                this.$log.error('Pagination', error);
            });
        }
    }

    increasePaginationEnd() {
        // If we're on the last page, set the end to the total number of items
        if (this.currentPage == this.pageTotal) {
            this.progressEnd = this.progressTotal;
        } else {
            // Otherwise increment by page size
            this.progressEnd = this.progressEnd + this.pageSize;
        }
        // Find all elements that need to be updated with the correct number
        const elements = document.querySelectorAll('.pagination-end');
        for (const element of elements) {
            element.innerHTML = String(this.progressEnd);
        }
    }

    reducePaginationStart() {
        // Find all elements that need to be updated with the correct number
        const elements = document.querySelectorAll('.pagination-start');
        for (const element of elements) {
            element.innerHTML = String(Number(element.innerHTML) - this.pageSize);
        }
    }

    updateBarPercentage(start: number, end: number, total: number) {
        // If a bar element exists, update it
        if (this.progressBar) {
            const percentage = ((end - start) / total) * 100;
            this.progressBar.style.width = `${percentage}%`;
        }
    }

    addNewElements(elements: Element[]) {
        // Loop through each new element
        for (const element of elements) {
            // Check for preload attributes - update image src's
            if (element.querySelector('[data-preload]')) {
                const preloads = element.querySelectorAll('[data-preload]');
                for (const p of preloads) {
                    p.setAttribute('src', String(p.getAttribute('data-preload')));
                    p.removeAttribute('data-preload');
                }
            }
            // Add new element to the DOM
            if (this.itemContainer) {
                this.itemContainer.appendChild(element);
            }
            // Update button text
            this.button.innerHTML = this.text;
        }
    }

}
